import { Input, Select, Space, Typography } from "antd";
import { useState } from "react";
import { describeCron } from "@/common/utils";

const CUSTOM = "__custom__";

interface Preset {
	label: string;
	value: string;
}

const PRESET_GROUPS: { label: string; options: Preset[] }[] = [
	{
		label: "Minutes",
		options: [
			{ label: "Every 15 minutes", value: "cron(0/15 * * * ? *)" },
			{ label: "Every 30 minutes", value: "cron(0/30 * * * ? *)" },
		],
	},
	{
		label: "Hourly",
		options: [{ label: "Every hour", value: "cron(0 * * * ? *)" }],
	},
	{
		label: "Daily",
		options: [
			{ label: "Every day at midnight (UTC)", value: "cron(0 0 * * ? *)" },
			{ label: "Every day at 8am (UTC)", value: "cron(0 8 * * ? *)" },
			{ label: "Every day at noon (UTC)", value: "cron(0 12 * * ? *)" },
		],
	},
	{
		label: "Weekly",
		options: [
			{
				label: "Every weekday (Mon–Fri) at 9am (UTC)",
				value: "cron(0 9 ? * MON-FRI *)",
			},
			{ label: "Every Monday at 9am (UTC)", value: "cron(0 9 ? * MON *)" },
			{ label: "Every Sunday at midnight (UTC)", value: "cron(0 0 ? * SUN *)" },
		],
	},
	{
		label: "Monthly",
		options: [
			{
				label: "First day of month at midnight (UTC)",
				value: "cron(0 0 1 * ? *)",
			},
		],
	},
	{
		label: "Advanced",
		options: [{ label: "Custom expression…", value: CUSTOM }],
	},
];

const ALL_PRESETS = PRESET_GROUPS.flatMap((g) => g.options);

function isPreset(v: string) {
	return ALL_PRESETS.some((p) => p.value === v && p.value !== CUSTOM);
}

interface CronPickerProps {
	value?: string;
	onChange?: (v: string) => void;
}

/** Form-compatible cron expression picker with presets and a custom entry mode. */
export function CronPicker({ value = "", onChange }: CronPickerProps) {
	const [customMode, setCustomMode] = useState(
		!isPreset(value) && value !== "",
	);

	const selectValue = customMode ? CUSTOM : value || undefined;
	const preview = value ? describeCron(value) : null;

	const handleSelect = (v: string) => {
		if (v === CUSTOM) {
			setCustomMode(true);
			onChange?.("");
		} else {
			setCustomMode(false);
			onChange?.(v);
		}
	};

	return (
		<Space vertical style={{ width: "100%" }} size={4}>
			<Select
				value={selectValue}
				onChange={handleSelect}
				placeholder="Select a schedule…"
				style={{ width: "100%" }}
				options={PRESET_GROUPS.map((g) => ({
					label: g.label,
					options: g.options,
				}))}
			/>
			{customMode && (
				<Input
					value={value}
					onChange={(e) => onChange?.(e.target.value)}
					placeholder="cron(0 8 * * ? *)"
					spellCheck={false}
					autoComplete="off"
				/>
			)}
			{preview && (
				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					{preview}
				</Typography.Text>
			)}
		</Space>
	);
}
